import { z } from 'zod';
import {
  logFeeding, upsertAnimal, createSchedule, updateSchedule, snoozeSchedule,
  createTimer, closeTimer,
} from '@/lib/mutations';
import { db } from '@/lib/db';
import { enqueue } from '@/lib/outbox';
import { reloadFromCache } from '@/state/data';
import type { ProposalAction } from '@/agent/types';
import type { Report, Schedule } from '@/types';

/** Client-side validation + execution of agent proposals.
 *  Every write flows through the same outbox as manual UI actions. */

const uuid = z.string().uuid();
const hhmm = z.string().regex(/^\d{2}:\d{2}(:\d{2})?$/);

const schemas: Record<ProposalAction['type'], z.ZodTypeAny> = {
  log_feeding: z.object({
    animal_id: uuid,
    schedule_id: uuid.nullish(),
    amount: z.number().nullish(),
    unit: z.string().nullish(),
    food: z.string().nullish(),
    notes: z.string().nullish(),
    fed_by: z.string().nullish(),
    fed_at: z.string().nullish(),
    skipped: z.boolean().nullish(),
  }),
  create_animal: z.object({
    name: z.string().min(1),
    species: z.string().nullish(),
    category: z.enum(['bird', 'raptor', 'mammal', 'reptile', 'rabbit', 'other']).nullish(),
    kind: z.enum(['individual', 'batch']).nullish(),
    count: z.number().int().min(1).nullish(),
    life_stage: z.string().nullish(),
    condition: z.enum(['stable', 'watch', 'critical']).nullish(),
    diet: z.string().nullish(),
    diet_notes: z.string().nullish(),
    notes: z.string().nullish(),
  }),
  update_animal: z.object({ animal_id: uuid, patch: z.record(z.unknown()) }),
  archive_animal: z.object({
    animal_id: uuid,
    status: z.enum(['released', 'deceased', 'archived']),
  }),
  create_schedule: z.object({
    animal_id: uuid.nullish(),
    new_animal_index: z.number().int().min(0).nullish(),
    stype: z.enum(['interval', 'times']),
    interval_minutes: z.number().int().min(1).max(10080).nullish(),
    times: z.array(hhmm).nullish(),
    active_start: hhmm.nullish(),
    active_end: hhmm.nullish(),
    feed_every_n_days: z.number().int().min(2).max(30).nullish(),
    days_of_week: z.array(z.number().int().min(0).max(6)).nullish(),
    amount: z.number().nullish(),
    unit: z.string().nullish(),
    food: z.string().nullish(),
    prep_notes: z.string().nullish(),
    anchor_at: z.string().nullish(),
  }).refine((v) => v.animal_id || v.new_animal_index != null, 'needs animal'),
  update_schedule: z.object({ schedule_id: uuid, patch: z.record(z.unknown()) }),
  pause_schedule: z.object({ schedule_id: uuid }),
  snooze: z.object({ schedule_id: uuid, minutes: z.number().int().min(1).max(1440) }),
  create_timer: z.object({
    label: z.string().min(1),
    minutes_from_now: z.number().min(1).max(2880).nullish(),
    due_at: z.string().nullish(),
    animal_id: uuid.nullish(),
  }).refine((v) => v.minutes_from_now != null || v.due_at, 'needs a time'),
  cancel_timer: z.object({ timer_id: uuid }),
  record_weight: z.object({
    animal_id: uuid,
    weight_g: z.number().positive(),
    notes: z.string().nullish(),
    fed_at: z.string().nullish(),
  }),
  save_report: z.object({
    rtype: z.enum(['handoff', 'daily', 'weekly', 'per_animal', 'audit']).default('handoff'),
    title: z.string().min(1),
    content_md: z.string().min(1),
  }),
};

/** Tolerate common LLM param aliases (esp. from OCR extraction). */
export function normalizeAction(a: ProposalAction): ProposalAction {
  const p = { ...a.params } as Record<string, unknown>;
  if (a.type === 'record_weight') {
    if (p.weight != null && p.weight_g == null) p.weight_g = p.weight;
    if (p.measured_at != null && p.fed_at == null) p.fed_at = p.measured_at;
    delete p.weight;
    delete p.unit;
    delete p.measured_at;
  }
  if (a.type === 'create_animal') {
    if (p.age != null && p.life_stage == null) p.life_stage = p.age;
    delete p.age;
    // guess a category from species when missing
    if (p.category == null && typeof p.species === 'string') {
      const s = (p.species as string).toLowerCase();
      p.category = /owl|hawk|falcon|kestrel|eagle|vulture|osprey/.test(s)
        ? 'raptor'
        : /rabbit|cottontail|hare/.test(s)
          ? 'rabbit'
          : /turtle|snake|lizard|salamander|frog|toad/.test(s)
            ? 'reptile'
            : /squirrel|opossum|possum|raccoon|fox|deer|woodchuck|skunk|otter|bat|chipmunk|groundhog/.test(s)
              ? 'mammal'
              : /bird|jay|robin|finch|dove|duck|goose|sparrow|cardinal|wren|swallow|mallard|crow|heron/.test(s)
                ? 'bird'
                : 'other';
    }
  }
  if (a.type === 'create_schedule') {
    delete p.notes; // prep notes ride in prep_notes
  }
  if (a.type === 'save_report') {
    const rt = String(p.rtype ?? '');
    if (!['handoff', 'daily', 'weekly', 'per_animal', 'audit'].includes(rt)) {
      p.rtype = /week/i.test(rt) ? 'weekly' : /daily|day/i.test(rt) ? 'daily' : /animal/i.test(rt) ? 'per_animal' : /audit/i.test(rt) ? 'audit' : 'handoff';
    }
    if (!p.title && typeof p.content_md === 'string') {
      const heading = (p.content_md as string).match(/^#{1,3}\s+(.+)/m)?.[1];
      p.title = heading ?? a.label ?? 'Shift handoff';
    }
  }
  return { ...a, params: p };
}

export function validateAction(raw: ProposalAction): { ok: boolean; error?: string } {
  const a = normalizeAction(raw);
  const schema = schemas[a.type];
  if (!schema) return { ok: false, error: `unknown action ${a.type}` };
  const r = schema.safeParse(a.params);
  return r.success ? { ok: true } : { ok: false, error: r.error.issues[0]?.message };
}

export interface ApplyResult {
  applied: string[]; // labels
  reportId?: string;
  feedLogIds: string[];
}

/** Execute checked actions in order. create_animal results are tracked so a
 *  later create_schedule with new_animal_index can attach to them. */
/** Execute actions. `checked` = indices into the FULL list to apply (default
 *  all) — kept index-stable so new_animal_index references survive unchecked
 *  rows. */
export async function applyActions(rawActions: ProposalAction[], checked?: number[]): Promise<ApplyResult> {
  const createdByIndex = new Map<number, string>(); // action index -> animal id
  const createdSequential: string[] = [];           // fallback: Nth create_animal
  const result: ApplyResult = { applied: [], feedLogIds: [] };
  const actions = rawActions.map(normalizeAction);
  const checkedSet = new Set(checked ?? actions.map((_, i) => i));

  const resolveNewAnimal = (idx: number): string | undefined =>
    createdByIndex.get(idx) ?? createdSequential[idx];

  for (let ai = 0; ai < actions.length; ai++) {
    if (!checkedSet.has(ai)) continue;
    const a = actions[ai];
    const p = a.params as Record<string, never>;
    switch (a.type) {
      case 'log_feeding': {
        const id = await logFeeding({
          animalId: p['animal_id'],
          scheduleId: p['schedule_id'] ?? null,
          amount: p['amount'] ?? null,
          unit: p['unit'] ?? null,
          food: p['food'] ?? null,
          notes: p['notes'] ?? null,
          fedBy: p['fed_by'] ?? null,
          fedAt: p['fed_at'] ? new Date(p['fed_at']) : undefined,
          skipped: p['skipped'] ?? false,
          source: 'agent',
        });
        result.feedLogIds.push(id);
        break;
      }
      case 'create_animal': {
        const id = await upsertAnimal({
          name: p['name'],
          species: p['species'] ?? null,
          category: p['category'] ?? 'other',
          kind: p['kind'] ?? 'individual',
          count: p['count'] ?? 1,
          life_stage: p['life_stage'] ?? null,
          condition: p['condition'] ?? 'stable',
          diet: p['diet'] ?? null,
          diet_notes: p['diet_notes'] ?? null,
          notes: p['notes'] ?? null,
          intake_date: new Date().toISOString().slice(0, 10),
        });
        createdByIndex.set(ai, id);
        createdSequential.push(id);
        break;
      }
      case 'update_animal': {
        const existing = await db.animals.get(p['animal_id'] as string);
        if (existing) await upsertAnimal({ ...existing, ...(p['patch'] as object), name: existing.name }, existing.id);
        break;
      }
      case 'archive_animal': {
        const existing = await db.animals.get(p['animal_id'] as string);
        if (existing) {
          await upsertAnimal(
            {
              name: existing.name,
              status: p['status'],
              released_date: p['status'] === 'released' ? new Date().toISOString().slice(0, 10) : null,
            },
            existing.id
          );
        }
        break;
      }
      case 'create_schedule': {
        const animalId =
          (p['animal_id'] as string) ?? resolveNewAnimal(p['new_animal_index'] as number);
        if (!animalId) {
          // its create_animal row was unchecked — skip quietly rather than
          // aborting the rest of the batch
          continue;
        }
        await createSchedule({
          animal_id: animalId,
          label: 'feed',
          stype: p['stype'],
          interval_minutes: p['interval_minutes'] ?? null,
          times: (p['times'] as string[] | null) ?? null,
          active_start: p['active_start'] ?? null,
          active_end: p['active_end'] ?? null,
          feed_every_n_days: p['feed_every_n_days'] ?? null,
          days_of_week: (p['days_of_week'] as number[] | null) ?? [0, 1, 2, 3, 4, 5, 6],
          fasting_dates: [],
          amount: p['amount'] ?? null,
          unit: p['unit'] ?? null,
          food: p['food'] ?? null,
          prep_notes: p['prep_notes'] ?? null,
          anchor_at: (p['anchor_at'] as string) ?? new Date().toISOString(),
          enabled: true,
        });
        break;
      }
      case 'update_schedule':
        await updateSchedule(p['schedule_id'], p['patch'] as Partial<Schedule>);
        break;
      case 'pause_schedule':
        await updateSchedule(p['schedule_id'], { enabled: false });
        break;
      case 'snooze':
        await snoozeSchedule(p['schedule_id'], p['minutes']);
        break;
      case 'create_timer': {
        const dueAt = p['due_at']
          ? new Date(p['due_at'])
          : new Date(Date.now() + (p['minutes_from_now'] as number) * 60_000);
        await createTimer(p['label'], dueAt, p['animal_id'] ?? null);
        break;
      }
      case 'cancel_timer':
        await closeTimer(p['timer_id'], 'canceled');
        break;
      case 'record_weight': {
        const id = await logFeeding({
          animalId: p['animal_id'],
          weightG: p['weight_g'],
          notes: p['notes'] ?? null,
          fedAt: p['fed_at'] ? new Date(p['fed_at']) : undefined,
          source: 'agent',
        });
        result.feedLogIds.push(id);
        break;
      }
      case 'save_report': {
        const report: Report = {
          id: crypto.randomUUID(),
          rtype: p['rtype'] ?? 'handoff',
          title: p['title'],
          content_md: p['content_md'],
          params: {},
          created_at: new Date().toISOString(),
        };
        await db.reports.put(report);
        await reloadFromCache();
        await enqueue({ table: 'reports', op: 'insert', values: report as never });
        result.reportId = report.id;
        break;
      }
    }
    result.applied.push(a.label);
  }
  return result;
}
